<template>
    <nio-modal
        name="appointment"
        size="sm"
        :title="modalTitle"
        ref="modal"
        @closed="onClosed"
        @open="onOpen"
        @opened="onOpened"
    >
        <section class="space-y-6">
            <nio-input-group :label="$t('appointments.appointmentModal.contact')">
                <nio-input
                    :disabled="true"
                    :value="appointment.contact.fullName"
                    v-if="contactIsPrefilled"
                />
                <contact-select
                    ref="contact"
                    v-model="appointment.contact_id"
                    v-else
                />
            </nio-input-group>

            <div class="space-y-6 | md:flex md:space-x-6 md:space-y-0">
                <nio-input-group class="flex-1" :label="$t('appointments.appointmentModal.date')">
                    <nio-datepicker :placeholder="$t('appointments.appointmentModal.selectDate')" v-model="appointment.date" />
                </nio-input-group>

                <nio-input-group class="flex-1" :label="$t('appointments.appointmentModal.time')">
                    <nio-timepicker :placeholder="$t('appointments.appointmentModal.selectTime')" v-model="appointment.time" />
                </nio-input-group>
            </div>

            <nio-input-group :label="$t('appointments.appointmentModal.relatedVehicleMake')" v-if="authUser.related_id">
                <nio-input v-model="appointment.related_vehicle_make" />
            </nio-input-group>

            <nio-input-group :label="$t('appointments.appointmentModal.notes')">
                <nio-textarea
                    rows="3"
                    v-model="appointment.notes"
                />
            </nio-input-group>
        </section>

        <template #footer>
            <nio-button class="flex-1" :disabled="saving" @click="close">
                {{ $t('global.general.cancel') }}
            </nio-button>
            <nio-button
                class="flex-1"
                color="blue"
                :disabled="!valid"
                :loading="saving"
                @click="onSave"
            >
                {{ saveButtonText }}
            </nio-button>
        </template>
    </nio-modal>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import Appointment from '@/entities/Appointment.js';
    import ContactSelect from '@/components/ContactSelect.vue';

    export default {
        components: {
            ContactSelect,
        },

        data() {
            return {
                saving: false,
                appointment: new Appointment(),
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            valid() {
                return this.appointment.isValid();
            },

            modalTitle() {
                return this.isEdit ? this.$t('appointments.appointmentModal.editTitle') : this.$t('appointments.appointmentModal.addTitle');
            },

            isEdit() {
                return this.appointment.isExisting();
            },

            saveButtonText() {
                return this.isEdit ? this.$t('global.general.save') : this.$t('global.general.create');
            },

            contactIsPrefilled() {
                return !!this.appointment.contact.isExisting();
            },
        },

        methods: {
            ...mapActions('appointment', [
                'createAppointment',
                'updateAppointment',
            ]),

            async onSave() {
                if (!this.valid) {
                    return;
                }

                try {
                    this.saving = true;

                    if (this.isEdit) {
                        await this.onUpdate();
                    } else {
                        await this.onCreate();
                    }

                    this.close();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('global.errors.unknown'),
                    });

                    throw error;
                } finally {
                    this.saving = false;
                }
            },

            async onCreate() {
                await this.createAppointment(this.appointment);

                this.$notify.show({
                    type: 'success',
                    message: this.$t('appointments.appointmentModal.appointmentCreated'),
                });
            },

            async onUpdate() {
                await this.updateAppointment(this.appointment);

                this.$notify.show({
                    type: 'success',
                    message: this.$t('appointments.appointmentModal.appointmentUpdated'),
                });
            },

            onOpen({ contact, appointment } = {}) {
                this.appointment.fill({ contact, ...appointment });
            },

            onOpened() {
                if (this.$refs.contact) {
                    this.$refs.contact.open();
                }
            },

            onClosed() {
                this.$reset();
            },

            close() {
                this.$refs.modal.close();
            },
        },
    };
</script>
